import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, Eye, EyeOff, ArrowRight, Github, Chrome, Loader } from 'lucide-react';
import { authAPI } from '../services/api';
import './AuthModals.css';

// OTP Input Component
const OTPInput = ({ length = 6, value, onChange, disabled }) => {
    const inputs = useRef([]);

    useEffect(() => {
        if (inputs.current[0]) {
            inputs.current[0].focus();
        }
    }, []);

    const handleChange = (e, index) => {
        const val = e.target.value;
        if (!/^\d*$/.test(val)) return;

        const newValue = value.split('');
        newValue[index] = val.slice(-1);
        const newOtp = newValue.join('');
        onChange(newOtp);

        // Auto-focus next input
        if (val && index < length - 1) {
            inputs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !value[index] && index > 0) {
            inputs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, length);
        if (/^\d+$/.test(pastedData)) {
            onChange(pastedData.padEnd(length, ''));
            inputs.current[Math.min(pastedData.length, length - 1)].focus();
        }
    };

    return (
        <div className="otp-input-container">
            {Array(length).fill(0).map((_, index) => (
                <input
                    key={index}
                    ref={(el) => (inputs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="otp-input"
                    value={value[index] || ''}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    disabled={disabled}
                />
            ))}
        </div>
    );
};

// Login Modal
export const LoginModal = ({ isOpen, onClose, onSwitchToSignup }) => {
    const [socialLoading, setSocialLoading] = useState('');
    const [error, setError] = useState('');

    const handleSocialLogin = (provider) => {
        setError('');
        setSocialLoading(provider);
        window.location.href = authAPI.getSocialAuthURL(provider);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="modal-container"
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className="modal-close" onClick={onClose}>
                            <X size={20} />
                        </button>

                        <div className="modal-header">
                            <h2 className="modal-title">Welcome Back</h2>
                            <p className="modal-subtitle">
                                Continue with a trusted provider to access Jarvis.
                            </p>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="auth-provider-stack">
                            <button
                                className="social-button social-button-full"
                                disabled={!!socialLoading}
                                onClick={() => handleSocialLogin('google')}
                            >
                                <Chrome size={20} />
                                {socialLoading === 'google' ? 'Redirecting to Google...' : 'Continue with Google'}
                            </button>
                            <button
                                className="social-button social-button-full"
                                disabled={!!socialLoading}
                                onClick={() => handleSocialLogin('github')}
                            >
                                <Github size={20} />
                                {socialLoading === 'github' ? 'Redirecting to GitHub...' : 'Continue with GitHub'}
                            </button>
                        </div>

                        <p className="auth-panel-note">
                            Email and password sign-in is disabled to reduce disposable-email abuse and keep access cleaner.
                        </p>

                        <p className="modal-footer-text">
                            New to NetHyTech?{' '}
                            <button type="button" className="link-button" onClick={onSwitchToSignup}>
                                Create your account
                            </button>
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Signup Modal
export const SignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
    const [socialLoading, setSocialLoading] = useState('');
    const [error, setError] = useState('');

    const handleClose = () => {
        setError('');
        onClose();
    };

    const handleSocialLogin = (provider) => {
        setError('');
        setSocialLoading(provider);
        window.location.href = authAPI.getSocialAuthURL(provider);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleClose}
                >
                    <motion.div
                        className="modal-container"
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className="modal-close" onClick={handleClose}>
                            <X size={20} />
                        </button>

                        <div className="modal-header">
                            <h2 className="modal-title">Create Account</h2>
                            <p className="modal-subtitle">
                                Start with Google or GitHub and we&apos;ll create your account instantly.
                            </p>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="auth-provider-stack">
                            <button
                                className="social-button social-button-full"
                                disabled={!!socialLoading}
                                onClick={() => handleSocialLogin('google')}
                            >
                                <Chrome size={20} />
                                {socialLoading === 'google' ? 'Redirecting to Google...' : 'Sign up with Google'}
                            </button>
                            <button
                                className="social-button social-button-full"
                                disabled={!!socialLoading}
                                onClick={() => handleSocialLogin('github')}
                            >
                                <Github size={20} />
                                {socialLoading === 'github' ? 'Redirecting to GitHub...' : 'Sign up with GitHub'}
                            </button>
                        </div>

                        <p className="auth-panel-note">
                            By continuing, you agree to the <a href="/terms">Terms of Service</a> and{' '}
                            <a href="/privacy">Privacy Policy</a>.
                        </p>

                        <p className="modal-footer-text">
                            Already have an account?{' '}
                            <button type="button" className="link-button" onClick={onSwitchToLogin}>
                                Sign in
                            </button>
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Forgot Password Modal with OTP
export const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setError('');
        setNotice('');
        setLoading(true);

        try {
            const response = await authAPI.forgotPassword(email);
            const nextStep = response.nextStep || 'otp';
            const fallbackNotice = response.devOtp
                ? `Development OTP: ${response.devOtp}`
                : '';

            setNotice(fallbackNotice || response.message || '');

            if (nextStep !== 'otp') {
                return;
            }

            if (response.devOtp) {
                setOtp(response.devOtp);
            }

            setStep(2);
            setCountdown(60);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setError('');
        setLoading(true);

        try {
            await authAPI.resetPassword({ email, otp, password, confirmPassword });
            setStep(3);
        } catch (err) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setEmail('');
        setOtp('');
        setPassword('');
        setConfirmPassword('');
        setError('');
        setNotice('');
        setCountdown(0);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleClose}
                >
                    <motion.div
                        className="modal-container modal-small"
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className="modal-close" onClick={handleClose}>
                            <X size={20} />
                        </button>

                        {/* Step 1: Enter Email */}
                        {step === 1 && (
                            <>
                                <div className="modal-header">
                                    <h2 className="modal-title">Reset Password</h2>
                                    <p className="modal-subtitle">
                                        Enter your email and we'll send you an OTP
                                    </p>
                                </div>

                                {error && <div className="error-message">{error}</div>}
                                {notice && <div className="info-message">{notice}</div>}

                                <form className="modal-form" onSubmit={handleSendOTP}>
                                    <div className="form-group">
                                        <label className="form-label">Email</label>
                                        <div className="input-wrapper">
                                            <Mail size={18} className="input-icon" />
                                            <input
                                                type="email"
                                                className="form-input"
                                                placeholder="Enter your email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <motion.button
                                        type="submit"
                                        className="submit-button"
                                        whileHover={{ scale: loading ? 1 : 1.01 }}
                                        whileTap={{ scale: loading ? 1 : 0.99 }}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader size={18} className="spinner" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                Send OTP
                                                <ArrowRight size={18} />
                                            </>
                                        )}
                                    </motion.button>
                                </form>

                                <p className="modal-footer-text">
                                    Remember your password?{' '}
                                    <button type="button" className="link-button" onClick={onBackToLogin}>
                                        Back to login
                                    </button>
                                </p>
                            </>
                        )}

                        {/* Step 2: Enter OTP and New Password */}
                        {step === 2 && (
                            <>
                                <div className="modal-header">
                                    <h2 className="modal-title">Enter OTP</h2>
                                    <p className="modal-subtitle">
                                        Enter the code sent to {email}
                                    </p>
                                </div>

                                {error && <div className="error-message">{error}</div>}
                                {notice && <div className="info-message">{notice}</div>}

                                <form className="modal-form" onSubmit={handleResetPassword}>
                                    <OTPInput
                                        length={6}
                                        value={otp}
                                        onChange={setOtp}
                                        disabled={loading}
                                    />

                                    <div className="form-group">
                                        <label className="form-label">New Password</label>
                                        <div className="input-wrapper">
                                            <Lock size={18} className="input-icon" />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className="form-input"
                                                placeholder="Enter new password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Confirm Password</label>
                                        <div className="input-wrapper">
                                            <Lock size={18} className="input-icon" />
                                            <input
                                                type="password"
                                                className="form-input"
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <motion.button
                                        type="submit"
                                        className="submit-button"
                                        whileHover={{ scale: loading ? 1 : 1.01 }}
                                        whileTap={{ scale: loading ? 1 : 0.99 }}
                                        disabled={loading || otp.length !== 6}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader size={18} className="spinner" />
                                                Resetting...
                                            </>
                                        ) : (
                                            <>
                                                Reset Password
                                                <ArrowRight size={18} />
                                            </>
                                        )}
                                    </motion.button>
                                </form>
                            </>
                        )}

                        {/* Step 3: Success */}
                        {step === 3 && (
                            <div className="success-message">
                                <div className="success-icon">✓</div>
                                <h3>Password Reset!</h3>
                                <p>Your password has been reset successfully.</p>
                                <button
                                    className="submit-button"
                                    onClick={onBackToLogin}
                                    style={{ marginTop: '24px' }}
                                >
                                    Back to Login
                                </button>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
